import { useEffect, useState } from 'react';
import { searchProperties, type SearchRequest } from '../../../services/ragSearchService';

/**
 * A count "spec": a stable key + the exact search request whose `pagination.total`
 * we want to display (e.g. a property-type tile or a city card). The request is
 * intentionally the SAME shape the /search page fires, so the number we show
 * always matches what the user sees after they click through (no phantom counts).
 */
export interface CountSpec {
  key: string;
  request: SearchRequest;
}

// Listing totals are language-independent, so cache them process-wide and key
// purely on the request. This makes tab/section re-mounts and EN⇄AR switches
// instant and avoids re-hammering /api/search on every homepage visit.
const cache = new Map<string, number>();

function cacheKey(request: SearchRequest): string {
  return JSON.stringify(request);
}

/** The count could not be fetched: the caller omits the number (and its skeleton). */
export const COUNT_UNAVAILABLE = 'unavailable' as const;

/**
 * One count: the number, `null` while it is still loading, or COUNT_UNAVAILABLE
 * when the request failed. Loading and failed used to share `null`, so a count
 * that errored kept its skeleton pulsing for good.
 */
export type ListingCount = number | null | typeof COUNT_UNAVAILABLE;

/**
 * Fetch live listing counts for a set of specs in parallel, non-blocking.
 * Returns a map of key → ListingCount, so the caller can show a skeleton while a
 * count loads and omit the number when it is unavailable, never a fabricated one.
 * A failure is not cached: the next mount tries again.
 */
export function useListingCounts(specs: CountSpec[]): Record<string, ListingCount> {
  const [counts, setCounts] = useState<Record<string, ListingCount>>(() => {
    const initial: Record<string, ListingCount> = {};
    for (const s of specs) initial[s.key] = cache.get(cacheKey(s.request)) ?? null;
    return initial;
  });

  // Stable dependency: only refetch when the actual specs change.
  const signature = JSON.stringify(specs.map((s) => [s.key, s.request]));

  useEffect(() => {
    let cancelled = false;

    // Only the uncached specs need fetching; seed state from the warm cache first.
    const pending: CountSpec[] = [];
    for (const spec of specs) {
      const cached = cache.get(cacheKey(spec.request));
      if (cached !== undefined) {
        setCounts((prev) => (prev[spec.key] === cached ? prev : { ...prev, [spec.key]: cached }));
      } else {
        pending.push(spec);
      }
    }

    // Bounded concurrency: fetch counts a FEW at a time instead of firing all of
    // them at once. The homepage otherwise bursts ~15 concurrent /api/search calls
    // (type tiles + city cards + featured + map + projects) which overruns the dev
    // DB's small connection pool (:5432 session pooler) and 500s. A small worker
    // pool keeps the number of in-flight queries well under that limit. See
    // CONTEXT.md §4 (session-pooler concurrency).
    const CONCURRENCY = 3;
    let cursor = 0;
    const worker = async (): Promise<void> => {
      while (!cancelled && cursor < pending.length) {
        const spec = pending[cursor++];
        const ck = cacheKey(spec.request);
        try {
          const res = await searchProperties({ ...spec.request, limit: 1 });
          const total = res.success && res.data ? res.data.pagination.total : null;
          if (typeof total === 'number') {
            cache.set(ck, total);
            if (!cancelled) setCounts((prev) => ({ ...prev, [spec.key]: total }));
          } else if (!cancelled) {
            setCounts((prev) => ({ ...prev, [spec.key]: COUNT_UNAVAILABLE }));
          }
        } catch {
          // The UI omits the count, never a fake number, and stops its skeleton.
          if (!cancelled) setCounts((prev) => ({ ...prev, [spec.key]: COUNT_UNAVAILABLE }));
        }
      }
    };
    void Promise.all(Array.from({ length: Math.min(CONCURRENCY, pending.length) }, () => worker()));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return counts;
}
